import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { customerApi } from '../api/endpoints.ts';
import { ApiRequestError } from '../api/client.ts';
import { useSession } from '../state/session.tsx';
import { Alert, Button, TextArea, TextInput } from './ui.tsx';
import { getDateLocale } from '../i18n/locale.ts';
import { isSamePhoneNumber } from '@shared/phone.ts';
import { SMS_QUOTE_NOTE_MAX } from '@shared/constants/index.ts';
import type { Customer } from '@shared/types/index.ts';

/**
 * The customer's portal link: a standing page (/p/{token}) where they see
 * their upcoming visits, payment history and card on file.
 *
 * Portal access checks its token hash. Guarded text operations retain an
 * encrypted receipt so a lost reply can recover that operation's original
 * link. A new operation replaces it, which also revokes a leaked link.
 *
 * Emailing it is the same mint with a message attached, for the same reason a
 * quote is sent from its own editor: the office should not have to copy a URL
 * into their own mail client to get it to the person it belongs to.
 */
export function PortalLinkSection({ customer, onChanged }: { customer: Customer; onChanged: () => void }) {
  const { t } = useTranslation();
  const { can, company } = useSession();
  const [mintedUrl, setMintedUrl] = useState<string | null>(null);
  /** Who the link was just emailed to, when the last mint went out by email. */
  const [emailedTo, setEmailedTo] = useState<string | null>(null);
  /** The same, for a text. Only one of the two is ever set. */
  const [textedTo, setTextedTo] = useState<string | null>(null);
  /** Set when the last text is waiting for texting hours rather than already gone. */
  const [textDeferred, setTextDeferred] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /**
   * Starts from the address on the record but stays editable: the person who
   * arranges the cleaning is not always the one the record is filed under, and
   * a customer with no address on file would otherwise have no way to be sent
   * their own page.
   */
  const [recipientEmail, setRecipientEmail] = useState(() => customer.email ?? '');
  /** The note that travels at the top of the email, above the link. */
  const [emailMessage, setEmailMessage] = useState('');
  /** The texting panel's own pair, on the same terms as the email one. */
  const [recipientPhone, setRecipientPhone] = useState(() => customer.phone ?? '');
  const [textMessage, setTextMessage] = useState('');

  /**
   * Everything above belongs to the customer on screen, and this panel outlives
   * them: the detail page stays mounted when the customer around it changes, so
   * a one-shot initializer would leave the previous person's address sitting in
   * "Send to" — and this button mints a credential that carries the *new*
   * customer's visits, payments and card on file. Sending that to whoever was
   * on screen a moment ago is the one mistake this panel must not make.
   *
   * `BillingCard` guards its own state the same way, for the milder version of
   * the same problem.
   */
  useEffect(() => {
    setRecipientEmail(customer.email ?? '');
    setEmailMessage('');
    setRecipientPhone(customer.phone ?? '');
    setTextMessage('');
    setMintedUrl(null);
    setEmailedTo(null);
    setTextedTo(null);
    setCopied(false);
    setError(null);
    // Deliberately keyed on the id alone: re-running whenever `customer.email`
    // changes would also fire on the refetch that follows a send, wiping the
    // one-time URL out of the success panel — and would overwrite an address
    // typed here with the record's whenever anything else about the customer
    // was saved.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer.id]);

  const describeError = (caught: unknown, fallback: string) =>
    setError(caught instanceof ApiRequestError ? caught.message : fallback);

  const mint = useMutation({
    mutationFn: () => customerApi.createPortalLink(customer.id),
    onSuccess: (result) => {
      setError(null);
      setMintedUrl(result.url);
      setEmailedTo(null);
      setTextedTo(null);
      onChanged();
    },
    onError: (caught: unknown) => describeError(caught, t('portalLink.error')),
  });

  const emailLink = useMutation({
    mutationFn: () =>
      customerApi.emailPortalLink(customer.id, {
        recipientEmail: recipientEmail.trim() || undefined,
        message: emailMessage.trim() || undefined,
      }),
    onSuccess: (result) => {
      setError(null);
      setMintedUrl(result.url);
      setEmailedTo(result.queuedFor);
      setTextedTo(null);
      setTextDeferred(false);
      // The note belonged to the message that just went out; leaving it in the
      // box makes it look like it is still pending, and a second send would
      // repeat it.
      setEmailMessage('');
      onChanged();
    },
    onError: (caught: unknown) => describeError(caught, t('portalLink.emailError')),
  });

  const textLink = useMutation({
    mutationFn: () =>
      customerApi.textPortalLink(customer.id, {
        recipientPhone: recipientPhone.trim() || undefined,
        message: textMessage.trim() || undefined,
      }),
    onSuccess: (result) => {
      setError(null);
      setMintedUrl(result.url);
      setTextedTo(result.delivered || result.deferredUntilOpenHours ? result.queuedFor : null);
      setTextDeferred(result.deferredUntilOpenHours);
      setEmailedTo(null);
      setTextMessage('');
      // Quiet hours is not a failure — the link is live either way — so it is
      // shown in the success panel below rather than as an error here. A text
      // the provider refused still replaced the link, though, so that is an
      // error the sender has to see rather than a quiet non-event.
      if (result.textUnconfirmed) {
        setError(t('portalLink.textUnconfirmed', { recipient: result.queuedFor }));
      } else if (!result.delivered && !result.deferredUntilOpenHours) {
        setError(t('portalLink.textNotDelivered', { recipient: result.queuedFor }));
      } else if (result.noteTruncated) {
        setError(t('portalLink.textTruncated'));
      }
      onChanged();
    },
    onError: (caught: unknown) => describeError(caught, t('portalLink.textError')),
  });

  const revoke = useMutation({
    mutationFn: () => customerApi.revokePortalLink(customer.id),
    onSuccess: () => {
      setError(null);
      setMintedUrl(null);
      setEmailedTo(null);
      setTextedTo(null);
      setTextDeferred(false);
      onChanged();
    },
    onError: (caught: unknown) => describeError(caught, t('portalLink.error')),
  });

  async function copyUrl() {
    if (!mintedUrl) return;
    try {
      await navigator.clipboard.writeText(mintedUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError(t('portalLink.copyError'));
    }
  }

  if (!can('customers:write')) return null;

  const emailRecipient = recipientEmail.trim();
  const busy = mint.isPending || emailLink.isPending || textLink.isPending || revoke.isPending;
  /**
   * A link this customer would lose if another were minted.
   *
   * `mintedUrl` counts as well as the server's flag: a link minted seconds ago
   * in this very panel is live whether or not the parent's refetch has landed
   * yet — and if that refetch failed, the flag never arrives at all. Reading
   * only the flag skipped the warning in exactly the case where the reader is
   * most likely to still be looking at the URL they just copied.
   */
  const hasLiveLink = customer.portalLinkActive || mintedUrl !== null;

  /**
   * Whether the texting panel is worth showing, and if so whether the button
   * can do anything.
   *
   * The panel is gated on the company — a company with no texting anywhere in
   * the app has no use for it — while the *reason* a particular customer cannot
   * be texted is carried by the button, so "why can I email this and not text
   * it?" has an answer on screen rather than a missing panel.
   */
  const companyCanText = Boolean(company?.smsConfigured) && Boolean(company?.smsRemindersEnabled);
  const textRecipient = recipientPhone.trim();
  const consentCoversNumber =
    customer.smsConsentStatus === 'opted_in' &&
    isSamePhoneNumber(customer.smsConsentPhone, textRecipient, company?.countryCode ?? 'US');
  const textBlockedReason = !textRecipient
    ? t('portalLink.textNoNumber')
    : !consentCoversNumber
      ? t('portalLink.textNoConsent')
      : // Only when the lookup has actually answered about *this* number; a
        // null means nobody has asked, which is not a reason to refuse.
        customer.phoneLineType === 'landline'
        ? t('portalLink.textLandline')
        : null;

  return (
    <section className="card mb-6" aria-labelledby="portal-link-heading">
      <h2 id="portal-link-heading" className="mb-2 text-lg font-semibold">
        {t('portalLink.heading')}
      </h2>
      <p className="mb-3 text-sm text-[color:var(--color-muted)]">{t('portalLink.description')}</p>

      {error ? (
        <div className="mb-3">
          <Alert tone="error">{error}</Alert>
        </div>
      ) : null}

      {mintedUrl ? (
        <div className="mb-3">
          <Alert
            tone="success"
            title={
              emailedTo || textedTo
                ? textedTo
                  ? t(textDeferred ? 'portalLink.textDeferredTitle' : 'portalLink.textedTitle')
                  : t('portalLink.emailedTitle')
                : t('portalLink.mintedTitle')
            }
          >
            <p className="mb-2">
              {textedTo
                ? t(textDeferred ? 'portalLink.textDeferredBody' : 'portalLink.textedBody', { recipient: textedTo })
                : emailedTo
                  ? t('portalLink.emailedBody', { recipient: emailedTo })
                  : t('portalLink.mintedBody')}
            </p>
            <p className="break-all font-mono text-xs">{mintedUrl}</p>
            <Button className="mt-2" onClick={() => void copyUrl()}>
              {copied ? t('common.copied') : t('common.copyLink')}
            </Button>
          </Alert>
        </div>
      ) : customer.portalLinkActive ? (
        <p className="mb-3 text-sm">
          {t('portalLink.activeSince', {
            date: customer.portalLinkCreatedAt
              ? new Intl.DateTimeFormat(getDateLocale()).format(new Date(customer.portalLinkCreatedAt))
              : '',
          })}
        </p>
      ) : (
        <p className="mb-3 text-sm text-[color:var(--color-muted)]">{t('portalLink.noneYet')}</p>
      )}

      <div className="flex flex-wrap gap-2">
        <Button busy={mint.isPending} disabled={busy} onClick={() => mint.mutate()}>
          {hasLiveLink ? t('portalLink.replace') : t('portalLink.create')}
        </Button>
        {customer.portalLinkActive ? (
          <Button
            variant="danger"
            busy={revoke.isPending}
            disabled={busy}
            onClick={() => {
              if (window.confirm(t('portalLink.confirmRevoke'))) revoke.mutate();
            }}
          >
            {t('portalLink.revoke')}
          </Button>
        ) : null}
      </div>

      {/* ---- Email it to the customer ------------------------------------ */}
      <div className="mt-6 border-t border-[color:var(--color-line)] pt-4">
        <h3 className="mb-1 font-semibold">{t('portalLink.emailHeading')}</h3>
        <p className="mb-3 text-sm text-[color:var(--color-muted)]">{t('portalLink.emailDescription')}</p>

        <div className="grid max-w-xl gap-1">
          <TextInput
            label={t('portalLink.sendToLabel')}
            type="email"
            hint={t('portalLink.sendToHint')}
            value={recipientEmail}
            onChange={(event) => setRecipientEmail(event.target.value)}
          />
          <TextArea
            label={t('portalLink.emailMessageLabel')}
            hint={t('portalLink.emailMessageHint')}
            rows={3}
            maxLength={4000}
            value={emailMessage}
            onChange={(event) => setEmailMessage(event.target.value)}
          />
          <div>
            <Button
              variant="primary"
              busy={emailLink.isPending}
              disabled={busy || !emailRecipient}
              onClick={() => {
                // The send mints, so an existing link dies the moment this one
                // is created — the customer's bookmark included. Asking first
                // is the difference between replacing a link on purpose and
                // finding out from the customer.
                if (!hasLiveLink || window.confirm(t('portalLink.confirmEmailReplace'))) {
                  emailLink.mutate();
                }
              }}
            >
              {hasLiveLink ? t('portalLink.sendEmailAgain') : t('portalLink.sendEmail')}
            </Button>
          </div>
        </div>
      </div>

      {/* ---- Text it to the customer ------------------------------------- */}
      {companyCanText ? (
        <div className="mt-6 border-t border-[color:var(--color-line)] pt-4">
          <h3 className="mb-1 font-semibold">{t('portalLink.textHeading')}</h3>
          <p className="mb-3 text-sm text-[color:var(--color-muted)]">{t('portalLink.textDescription')}</p>

          <div className="grid max-w-xl gap-1">
            <TextInput
              label={t('portalLink.textToLabel')}
              type="tel"
              hint={t('portalLink.textToHint')}
              value={recipientPhone}
              onChange={(event) => setRecipientPhone(event.target.value)}
            />
            <TextArea
              label={t('portalLink.textMessageLabel')}
              hint={t('portalLink.textMessageHint', { remaining: SMS_QUOTE_NOTE_MAX - textMessage.length })}
              rows={2}
              maxLength={SMS_QUOTE_NOTE_MAX}
              value={textMessage}
              onChange={(event) => setTextMessage(event.target.value)}
            />
            {textBlockedReason ? <p className="text-sm text-[color:var(--color-muted)]">{textBlockedReason}</p> : null}
            <div>
              <Button
                variant="primary"
                busy={textLink.isPending}
                disabled={busy || Boolean(textBlockedReason)}
                onClick={() => {
                  // Same warning as the email send, for the same reason: this
                  // mints, so the customer's existing bookmark dies either way.
                  if (!hasLiveLink || window.confirm(t('portalLink.confirmTextReplace'))) {
                    textLink.mutate();
                  }
                }}
              >
                {hasLiveLink ? t('portalLink.sendTextAgain') : t('portalLink.sendText')}
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
}
